import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";

const CustomerAcquisitionMetrics = () => {
  const { customerOffersData } = useSelector(
    (state) => state?.merchantReducer
  );

  const [chartData, setChartData] = useState({
    series: [
      {
        name: "Total New Users",
        data: [],
      },
    ],
    categories: [],
  });
  const [maxDataValue, setMaxDataValue] = useState(10); // Initial max value
  const [step, setStep] = useState(2); // Initial step value

  const MONTHS_ORDER = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  const convertMonthToAbbreviation = (monthString) => {
    const date = new Date(monthString + "-01");
    return date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  };

  useEffect(() => {
    if (customerOffersData?.success && customerOffersData?.data?.customerMetrics) {
      const metrics = customerOffersData.data.customerMetrics;

      // Convert months to abbreviations and format the data
      const formattedData = metrics.map((item) => ({
        month: convertMonthToAbbreviation(item.month),
        totalNewUsers: item.totalNewUsers,
      }));

      // Sort the formatted data based on the predefined month order
      const sortedData = formattedData.sort(
        (a, b) =>
          MONTHS_ORDER.indexOf(a.month) - MONTHS_ORDER.indexOf(b.month)
      );

      const dataValues = sortedData.map((item) => item.totalNewUsers);

      // Calculate max value and step dynamically
      const calculatedMaxDataValue = Math.max(...dataValues, 10);
      const calculatedStep = Math.ceil(calculatedMaxDataValue / 5);

      setMaxDataValue(calculatedMaxDataValue);
      setStep(calculatedStep);

      // Update chart data
      setChartData({
        series: [
          {
            name: "Total New Users",
            data: dataValues,
          },
        ],
        categories: sortedData.map((item) => item.month),
      });
    }
  }, [customerOffersData]);

  const options = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: ["#00ADEF"],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "area",
      dropShadow: {
        enabled: true,
        color: "#623CEA14",
        top: 10,
        blur: 4,
        left: 0,
        opacity: 0.1,
      },
      toolbar: {
        show: false,
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },
        },
      },
      {
        breakpoint: 1366,
        options: {
          chart: {
            height: 350,
          },
        },
      },
    ],
    stroke: {
      width: 2,
      curve: "smooth",
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
      colors: "#fff",
      strokeColors: ["#00ADEF"],
      strokeWidth: 3,
      strokeOpacity: 0.9,
      strokeDashArray: 0,
      fillOpacity: 1,
      hover: {
        size: undefined,
        sizeOffset: 5,
      },
    },
    xaxis: {
      type: "category",
      categories: chartData.categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      title: {
        text: "Time Period (Months)",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: Math.ceil(maxDataValue / step) * step,
      tickAmount: Math.ceil(maxDataValue / step),
      labels: {
        formatter: (value) => value,
      },
      title: {
        text: "New Users",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
  };

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 pt-7.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col sm:flex-col xl:flex-row justify-between w-full mt-4">
        <span className="text-[20px] font-poppins font-[600] text-[black] whitespace-nowrap">
          Customer Acquisition Metrics
        </span>
        {/* <div>
          <div className="relative z-20 inline-block">
            <select
              name="filter"
              id="filter"
              className="relative z-20 inline-flex appearance-none bg-transparent py-1 pl-3 pr-8 text-sm font-medium outline-none"
              value={state.selectedFilter} // Controlled dropdown value
              onChange={handleFilterChange} // Trigger when filter is changed
            >
              <option value="Last week" className="dark:bg-boxdark">
                Last week
              </option>
              <option value="This month" className="dark:bg-boxdark">
                This month
              </option>
              <option value="Past years" className="dark:bg-boxdark">
                Past years
              </option>
            </select>
            <span className="absolute top-1/2 right-3 z-10 -translate-y-1/2">
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.47072 1.08816C0.47072 1.02932 0.500141 0.955772 0.54427 0.911642C0.647241 0.808672 0.809051 0.808672 0.912022 0.896932L4.85431 4.60386C4.92785 4.67741 5.06025 4.67741 5.14851 4.60386L9.09079 0.896932C9.19376 0.793962 9.35557 0.808672 9.45854 0.911642C9.56151 1.01461 9.5468 1.17642 9.44383 1.27939L5.50155 4.98632C5.22206 5.23639 4.78076 5.23639 4.51598 4.98632L0.558981 1.27939C0.50014 1.22055 0.47072 1.16171 0.47072 1.08816Z"
                  fill="#637381"
                />
              </svg>
            </span>
          </div>
        </div> */}
      </div>

      {chartData.series[0]?.data.length > 0 ? (
        <div>
          <div id="chartOne" className="-ml-5 mt-4">
            <ReactApexChart
              options={options}
              series={chartData.series}
              type="area"
              height={350}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-[200px] flex items-center justify-center">
          <div id="chartTwo" className="min-w-[700px]">
            <div className="text-center text-gray-500 text-lg font-medium py-10">
              No New Users Found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAcquisitionMetrics;
